import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AnySoapField,
} from '@/components/voice/SoapField'
import type {
  FinalizeResponse,
  NoteEditResponse,
  SoapPayload,
  SoapNumberField,
  VisitFullResponse,
} from '@yui-receipt/shared'
import { api, ApiClientError, getToken } from '@/lib/api'
import { useAuth } from '@/store/auth'
import { toastError, toastSuccess } from '@/store/toast'
import Tabs, { type TabItem } from '@/components/voice/Tabs'
import SoapField from '@/components/voice/SoapField'
import LinkedEvidenceSheet from '@/components/voice/LinkedEvidenceSheet'
import AttestationModal from '@/components/voice/AttestationModal'
import { getDeviceFingerprint } from '@/lib/voicesoap/deviceFingerprint'

type TabId = 'subjective' | 'objective' | 'ap'

const TABS: ReadonlyArray<TabItem<TabId>> = [
  { id: 'subjective', label: 'S — ผู้ป่วยเล่า' },
  { id: 'objective', label: 'O — ตรวจร่างกาย' },
  { id: 'ap', label: 'A+P — สรุป & แผน' },
]

/**
 * Therapist-facing review screen for a visit draft. Three tabs mirror the
 * SOAP structure. Each field edits commit on blur via POST /visits/:id/edits
 * and the 🔊 button opens a bottom sheet with the transcript + audio excerpt
 * that produced the value (Abridge-style "linked evidence").
 *
 * NOTE: this page assumes the draft already exists — recording/transcription
 * is driven from elsewhere (new-visit flow). Escalation, attestation, and
 * finalize move to Day 9-12.
 */
export default function VisitReviewPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const user = useAuth((s) => s.user)
  const [tab, setTab] = useState<TabId>('subjective')
  const [evidence, setEvidence] = useState<{
    field: AnySoapField
    fieldPath: string
  } | null>(null)
  const [attestOpen, setAttestOpen] = useState(false)

  // Local overrides keep the UI responsive between blur → server round-trip.
  // Keyed by fieldPath so the render layer can prefer the edit over the draft.
  const edits = useRef(new Map<string, string | null>())

  // editDurationSec measured from when the review page first renders a draft —
  // not from visit start — so it captures "therapist-review time" for audit.
  const reviewStartedAt = useRef<number | null>(null)

  const { data, isLoading, error } = useQuery<VisitFullResponse>({
    queryKey: ['visit', id],
    queryFn: () => api(`/visits/${id}`),
    enabled: !!id,
  })

  const editMut = useMutation({
    mutationFn: (req: { fieldPath: string; before: string | null; after: string | null }) =>
      api<NoteEditResponse>(`/visits/${id}/edits`, {
        method: 'POST',
        body: req,
      }),
    onSuccess: (_res, vars) => {
      edits.current.set(vars.fieldPath, vars.after)
      toastSuccess('บันทึกการแก้ไข')
      // Refresh draft so reviewFlags/audits stay current, but don't block UI.
      qc.invalidateQueries({ queryKey: ['visit', id] })
    },
    onError: (err: unknown, vars) => {
      edits.current.delete(vars.fieldPath)
      const msg = err instanceof ApiClientError ? err.message : 'บันทึกไม่สำเร็จ'
      toastError(msg)
    },
  })

  const finalizeMut = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error('ยังไม่มี draft สำหรับเซ็นยืนยัน')
      const deviceFingerprint = await getDeviceFingerprint()
      const elapsedSec = reviewStartedAt.current
        ? Math.max(0, Math.round((Date.now() - reviewStartedAt.current) / 1000))
        : 0
      return api<FinalizeResponse>(`/visits/${id}/final`, {
        method: 'POST',
        body: {
          draftId: draft.id,
          json: draft.json,
          deviceFingerprint,
          editDurationSec: elapsedSec,
        },
      })
    },
    onSuccess: () => {
      toastSuccess('เซ็นยืนยันเรียบร้อย')
      qc.invalidateQueries({ queryKey: ['visit', id] })
      setAttestOpen(false)
      navigate(`/visits/${id}/final`, { replace: true })
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiClientError ? err.message : 'เซ็นยืนยันไม่สำเร็จ'
      toastError(msg)
    },
  })

  const draft = data?.draft ?? null
  const payload: SoapPayload | null = draft?.json ?? null
  const isFinalized = !!data?.final
  const transcriptText = data?.transcript?.text ?? ''
  const transcriptSegs = data?.transcript?.segmentsJson

  // Start the review timer the first time we see a draft. Subsequent re-fetches
  // (e.g. after a field edit) must not reset it, or the audit number drifts.
  useEffect(() => {
    if (draft && reviewStartedAt.current == null) {
      reviewStartedAt.current = Date.now()
    }
  }, [draft])

  // Authenticated audio URL — the /uploads/... endpoint expects the bearer token
  // as a query param (same pattern as the medical-cert PDF iframe).
  const audioUrl = useMemo(() => {
    const raw = data?.audio ? `/api/audio/${data.audio.id}/stream` : null
    if (!raw) return null
    const t = getToken()
    return t ? `${raw}?token=${encodeURIComponent(t)}` : raw
  }, [data?.audio])

  function handleCommit(fieldPath: string, before: string | null, after: string | null) {
    editMut.mutate({ fieldPath, before, after })
  }

  function openEvidence(field: AnySoapField, fieldPath: string) {
    setEvidence({ field, fieldPath })
  }

  if (isLoading) {
    return <p className="max-w-3xl mx-auto p-6 text-slate-500">กำลังโหลด…</p>
  }
  if (error || !data) {
    return (
      <p className="max-w-3xl mx-auto p-6 text-rose-600">
        โหลดไม่สำเร็จ —{' '}
        <Link to="/" className="underline">
          กลับ
        </Link>
      </p>
    )
  }
  if (!payload) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <p className="text-slate-600">
          ยังไม่มีโน้ตฉบับร่างสำหรับการรักษานี้ — อัดเสียงก่อนเพื่อสร้าง draft
        </p>
      </div>
    )
  }

  const flags = draft?.reviewFlags ?? []
  const needsReview = flags.length > 0

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">
            ตรวจทานโน้ต {data.visit.patientNameSnapshot}
          </h1>
          <p className="text-xs text-slate-500">
            HN {data.visit.patientHnSnapshot} • {data.visit.visitDate} •{' '}
            {data.visit.visitType === 'initial' ? 'ครั้งแรก' : 'ติดตามผล'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/visits" className="text-sm text-slate-500 hover:text-brand-600">
            ← กลับ
          </Link>
          {isFinalized ? (
            <Link
              to={`/visits/${id}/final`}
              className="btn-secondary min-h-tap text-sm"
            >
              🔒 ดูฉบับที่เซ็นแล้ว
            </Link>
          ) : (
            <button
              type="button"
              className="btn-primary min-h-tap text-sm"
              onClick={() => setAttestOpen(true)}
              disabled={!draft || finalizeMut.isPending}
            >
              ✓ เซ็นยืนยัน
            </button>
          )}
        </div>
      </header>

      {needsReview && (
        <div
          role="status"
          className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <strong>ต้องตรวจสอบ:</strong> AI ตั้งธงไว้{' '}
          {flags.map((f) => flagLabel(f)).join(' • ')}
        </div>
      )}

      <Tabs tabs={TABS} value={tab} onChange={setTab} />

      <section
        id={`tab-panel-${tab}`}
        role="tabpanel"
        aria-labelledby={`tab-${tab}`}
        className="space-y-4"
      >
        {tab === 'subjective' && (
          <SubjectiveTab
            data={payload.subjective}
            onCommit={handleCommit}
            onEvidenceTap={openEvidence}
          />
        )}
        {tab === 'objective' && (
          <ObjectiveTab
            data={payload.objective}
            onCommit={handleCommit}
            onEvidenceTap={openEvidence}
          />
        )}
        {tab === 'ap' && (
          <AssessmentPlanTab
            assessment={payload.assessment}
            plan={payload.plan}
            onCommit={handleCommit}
            onEvidenceTap={openEvidence}
          />
        )}
      </section>

      <LinkedEvidenceSheet
        open={!!evidence}
        onClose={() => setEvidence(null)}
        audioUrl={audioUrl}
        sourcePhrase={evidence?.field.sourcePhrase ?? null}
        transcriptStart={evidence?.field.transcriptStart ?? null}
        transcript={transcriptText}
        segments={transcriptSegs}
        label={evidence?.fieldPath.split('.').pop()}
      />

      <AttestationModal
        open={attestOpen}
        onClose={() => setAttestOpen(false)}
        therapistLicense={user?.therapistLicense ?? null}
        reviewFlagsLabel={
          flags.length > 0 ? flags.map((f) => flagLabel(f)).join(' • ') : undefined
        }
        submitting={finalizeMut.isPending}
        onConfirm={async () => {
          await finalizeMut.mutateAsync()
        }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab panels — small enough to keep colocated.
// ---------------------------------------------------------------------------

type TabProps = {
  onCommit: (fieldPath: string, before: string | null, after: string | null) => void
  onEvidenceTap: (field: AnySoapField, fieldPath: string) => void
}

function SubjectiveTab({
  data,
  onCommit,
  onEvidenceTap,
}: { data: SoapPayload['subjective'] } & TabProps) {
  return (
    <>
      <SoapField
        label="อาการสำคัญ"
        field={data.chiefComplaint}
        fieldPath="subjective.chiefComplaint"
        type="textarea"
        onCommit={onCommit}
        onEvidenceTap={onEvidenceTap}
      />
      <SoapField
        label="ระดับปวด (VAS 0-10)"
        field={data.painVas as unknown as SoapNumberField}
        fieldPath="subjective.painVas"
        type="number"
        min={0}
        max={10}
        onCommit={onCommit}
        onEvidenceTap={onEvidenceTap}
      />
      <SoapField
        label="ตำแหน่งปวด"
        field={data.painLocation}
        fieldPath="subjective.painLocation"
        onCommit={onCommit}
        onEvidenceTap={onEvidenceTap}
      />
      <SoapField
        label="ลักษณะอาการปวด"
        field={data.painQuality}
        fieldPath="subjective.painQuality"
        onCommit={onCommit}
        onEvidenceTap={onEvidenceTap}
      />
      <SoapField
        label="ระยะเวลา/เริ่มเป็น"
        field={data.onset}
        fieldPath="subjective.onset"
        onCommit={onCommit}
        onEvidenceTap={onEvidenceTap}
      />
      <ListEcho label="ปัจจัยกระตุ้น" items={data.aggravatingFactors} />
      <ListEcho label="ปัจจัยบรรเทา" items={data.relievingFactors} />
      <SoapField
        label="การใช้ชีวิตก่อนบาดเจ็บ"
        field={data.priorLevelOfFunction}
        fieldPath="subjective.priorLevelOfFunction"
        type="textarea"
        onCommit={onCommit}
        onEvidenceTap={onEvidenceTap}
      />
      <ListEcho label="เป้าหมายผู้ป่วย" items={data.patientGoals} />
      <SoapField
        label="โรคประจำตัว"
        field={data.pmh}
        fieldPath="subjective.pmh"
        type="textarea"
        onCommit={onCommit}
        onEvidenceTap={onEvidenceTap}
      />
      <ListEcho label="ยาที่ใช้" items={data.medications} />
      <ListEcho label="ประวัติแพ้ยา" items={data.allergies} />
    </>
  )
}

function ObjectiveTab({
  data,
  onCommit,
  onEvidenceTap,
}: { data: SoapPayload['objective'] } & TabProps) {
  return (
    <>
      <details className="card p-3" open>
        <summary className="cursor-pointer font-medium text-slate-900">
          สัญญาณชีพ
        </summary>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <SoapField
            label="BP systolic"
            field={data.vitals.bpSystolic}
            fieldPath="objective.vitals.bpSystolic"
            type="number"
            onCommit={onCommit}
            onEvidenceTap={onEvidenceTap}
          />
          <SoapField
            label="BP diastolic"
            field={data.vitals.bpDiastolic}
            fieldPath="objective.vitals.bpDiastolic"
            type="number"
            onCommit={onCommit}
            onEvidenceTap={onEvidenceTap}
          />
          <SoapField
            label="ชีพจร"
            field={data.vitals.pulse}
            fieldPath="objective.vitals.pulse"
            type="number"
            onCommit={onCommit}
            onEvidenceTap={onEvidenceTap}
          />
          <SoapField
            label="อัตราการหายใจ"
            field={data.vitals.rr}
            fieldPath="objective.vitals.rr"
            type="number"
            onCommit={onCommit}
            onEvidenceTap={onEvidenceTap}
          />
          <SoapField
            label="อุณหภูมิ (°C)"
            field={data.vitals.tempC}
            fieldPath="objective.vitals.tempC"
            type="number"
            onCommit={onCommit}
            onEvidenceTap={onEvidenceTap}
          />
          <SoapField
            label="SpO₂ (%)"
            field={data.vitals.spo2}
            fieldPath="objective.vitals.spo2"
            type="number"
            onCommit={onCommit}
            onEvidenceTap={onEvidenceTap}
          />
        </div>
      </details>

      <SoapField
        label="การสังเกต"
        field={data.observation}
        fieldPath="objective.observation"
        type="textarea"
        onCommit={onCommit}
        onEvidenceTap={onEvidenceTap}
      />

      <ReadonlyList label="ROM" lines={data.rom.map(romLine)} />
      <ReadonlyList label="MMT" lines={data.mmt.map(mmtLine)} />
      <ReadonlyList
        label="Special tests"
        lines={data.specialTests.map(specialTestLine)}
      />
      <ReadonlyList
        label="การรักษาวันนี้"
        lines={data.interventionsToday.map(interventionLine)}
      />
      <ReadonlyList
        label="Outcome measures"
        lines={data.outcomeMeasures.map(outcomeLine)}
      />
    </>
  )
}

function AssessmentPlanTab({
  assessment,
  plan,
  onCommit,
  onEvidenceTap,
}: {
  assessment: SoapPayload['assessment']
  plan: SoapPayload['plan']
} & TabProps) {
  return (
    <>
      <SoapField
        label="PT diagnosis"
        field={assessment.ptDiagnosis}
        fieldPath="assessment.ptDiagnosis"
        type="textarea"
        onCommit={onCommit}
        onEvidenceTap={onEvidenceTap}
      />
      <SoapField
        label="Clinical reasoning"
        field={assessment.clinicalReasoning}
        fieldPath="assessment.clinicalReasoning"
        type="textarea"
        onCommit={onCommit}
        onEvidenceTap={onEvidenceTap}
      />
      <SoapField
        label="Progress note"
        field={assessment.progressNote}
        fieldPath="assessment.progressNote"
        type="textarea"
        onCommit={onCommit}
        onEvidenceTap={onEvidenceTap}
      />
      <SoapField
        label="เหตุผลการรักษา (skilled)"
        field={assessment.skilledRationale}
        fieldPath="assessment.skilledRationale"
        type="textarea"
        onCommit={onCommit}
        onEvidenceTap={onEvidenceTap}
      />

      <hr className="border-slate-200" />

      <ReadonlyList label="เป้าหมาย" lines={plan.goals.map(goalLine)} />
      <ReadonlyList label="แผนการรักษา" lines={plan.interventionsPlanned} />
      <SoapField
        label="ความถี่"
        field={plan.frequency}
        fieldPath="plan.frequency"
        onCommit={onCommit}
        onEvidenceTap={onEvidenceTap}
      />
      <SoapField
        label="ระยะเวลา (สัปดาห์)"
        field={plan.durationWeeks}
        fieldPath="plan.durationWeeks"
        type="number"
        min={0}
        onCommit={onCommit}
        onEvidenceTap={onEvidenceTap}
      />
      <ReadonlyList label="HEP" lines={plan.hep} />
      <ReadonlyList label="คำแนะนำผู้ป่วย" lines={plan.patientEducation} />
      <SoapField
        label="นัดครั้งถัดไป"
        field={plan.nextVisit}
        fieldPath="plan.nextVisit"
        onCommit={onCommit}
        onEvidenceTap={onEvidenceTap}
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ListEcho({ label, items }: { label: string; items: string[] }) {
  if (!items || items.length === 0) {
    return (
      <div>
        <div className="label">{label}</div>
        <p className="text-xs text-slate-500">ไม่พบในเสียง</p>
      </div>
    )
  }
  return (
    <div>
      <div className="label">{label}</div>
      <ul className="list-disc pl-5 text-sm text-slate-700">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  )
}

function ReadonlyList({ label, lines }: { label: string; lines: string[] }) {
  if (!lines || lines.length === 0) return null
  return (
    <div>
      <div className="label">{label}</div>
      <ul className="list-disc pl-5 text-sm text-slate-700 space-y-0.5">
        {lines.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ul>
    </div>
  )
}

function sideLabel(s: string): string {
  switch (s) {
    case 'left':
      return 'ซ้าย'
    case 'right':
      return 'ขวา'
    case 'bilateral':
      return 'สองข้าง'
    default:
      return ''
  }
}

function romLine(e: SoapPayload['objective']['rom'][number]): string {
  const side = sideLabel(e.side)
  const deg = e.degrees == null ? '—' : `${e.degrees}°`
  return [`${e.joint} ${e.movement}`, side, deg].filter(Boolean).join(' ')
}

function mmtLine(e: SoapPayload['objective']['mmt'][number]): string {
  const side = sideLabel(e.side)
  const grade = e.grade == null ? '—' : `${e.grade}/5`
  return [e.muscle, side, grade].filter(Boolean).join(' ')
}

function specialTestLine(e: SoapPayload['objective']['specialTests'][number]): string {
  const result =
    e.result === 'positive' ? 'Positive' : e.result === 'negative' ? 'Negative' : 'Inconclusive'
  return e.note ? `${e.testName} — ${result} (${e.note})` : `${e.testName} — ${result}`
}

function interventionLine(
  e: SoapPayload['objective']['interventionsToday'][number],
): string {
  const parts = [e.name, e.region, e.parameters].filter((p) => p && p.length > 0)
  return parts.join(' • ')
}

function outcomeLine(e: SoapPayload['objective']['outcomeMeasures'][number]): string {
  const val = e.value == null ? '—' : e.value
  return `${e.metric}: ${val} ${e.unit}`
}

function goalLine(g: SoapPayload['plan']['goals'][number]): string {
  const type = g.type === 'short_term' ? 'ST' : 'LT'
  const range =
    g.from != null && g.to != null ? ` (${g.from}→${g.to}${g.unit ? ' ' + g.unit : ''})` : ''
  const weeks = g.timeframeWeeks != null ? ` ใน ${g.timeframeWeeks} สัปดาห์` : ''
  return `[${type}] ${g.targetActivity}${range}${weeks}`
}

function flagLabel(f: string): string {
  switch (f) {
    case 'low_confidence_vitals':
      return 'สัญญาณชีพไม่ชัด'
    case 'unclear_rom':
      return 'ROM ไม่ชัด'
    case 'missing_vas':
      return 'ไม่มี VAS'
    case 'english_heavy':
      return 'ภาษาอังกฤษเยอะ'
    case 'short_audio':
      return 'เสียงสั้น'
    case 'number_not_found_in_transcript':
      return 'ตัวเลขไม่ตรงกับเสียง'
    case 'safety_block_partial':
      return 'มีส่วนที่ต้อง safety review'
    default:
      return f
  }
}

